import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, MapPin, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useCart } from "@/context/CartContext";
import { ProductImage } from "@/components/ProductImage";
import { fmt, PAYMENT_METHODS } from "@/lib/site";

export default function Checkout() {
    const { items, subtotal, iva, total, savings, unitPrice, clear } = useCart();
    const nav = useNavigate();
    const [form, setForm] = useState({ nombre: "", telefono: "", email: "", direccion: "", ciudad: "", tipo: "detal", entrega: "envio" });
    const retira = form.entrega === "retiro";
    const [metodo, setMetodo] = useState("pago_movil");
    const [notas, setNotas] = useState("");
    const [loading, setLoading] = useState(false);
    // Ubicación exacta para el despacho: en Venezuela la dirección escrita no
    // ubica; el link de Waze sí. GPS del teléfono o link pegado de Maps/Waze.
    const [ubi, setUbi] = useState("");
    const [ubiEstado, setUbiEstado] = useState("");
    // Los datos para pagar se muestran AQUÍ, al elegir el método: si el cliente
    // tiene que confirmar el pedido primero para saber a dónde pagar, se va.
    const [pagos, setPagos] = useState({});
    const [copiado, setCopiado] = useState("");

    useEffect(() => {
        api.get("/settings/payments").then((r) => setPagos(r.data || {})).catch(() => {});
    }, []);

    const copiar = async (texto) => {
        try {
            await navigator.clipboard.writeText(texto);
            setCopiado(texto);
            setTimeout(() => setCopiado(""), 1800);
        } catch {
            toast.error("No se pudo copiar; selecciona el texto manualmente");
        }
    };

    const capturarUbicacion = () => {
        if (!navigator.geolocation) return setUbiEstado("Tu navegador no da acceso al GPS; pega un link de Maps o Waze.");
        setUbiEstado("Capturando ubicación…");
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                setUbi(`https://waze.com/ul?ll=${latitude.toFixed(6)},${longitude.toFixed(6)}&navigate=yes`);
                setUbiEstado("ok");
            },
            () => setUbiEstado("No se pudo leer el GPS; pega un link de Maps o Waze."),
            { enableHighAccuracy: true, timeout: 12000 }
        );
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.nombre || !form.telefono) return toast.error("Completa tu nombre y teléfono");
        if (items.length === 0) return toast.error("Tu carrito está vacío");
        setLoading(true);
        try {
            if (window.fbq) window.fbq("track", "InitiateCheckout", { value: total, currency: "USD", num_items: items.length });
            const notasFinal = ubi ? `${notas ? notas + "\n" : ""}📍 Waze: ${ubi}` : notas;
            const payload = {
                cliente: form,
                items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
                metodo_pago: metodo,
                notas: notasFinal,
            };
            const { data } = await api.post("/orders", payload);
            clear();
            nav("/pedido-exitoso", { state: { order: data } });
        } catch (err) {
            toast.error("No se pudo crear el pedido", { description: err?.response?.data?.detail || "Intenta de nuevo" });
        } finally {
            setLoading(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="bg-crema min-h-screen">
                <Navbar />
                <div className="pt-40 pb-40 text-center px-5">
                    <h1 className="font-display font-bold text-3xl text-ink">Tu carrito está vacío</h1>
                    <button onClick={() => nav("/tienda")} className="mt-6 bg-verde text-white rounded-full px-8 py-4 font-medium hover:bg-verde-800 transition-colors">Ir a la tienda</button>
                </div>
            </div>
        );
    }

    const inputCls = "w-full bg-hueso border border-linea focus:border-madera rounded-xl outline-none py-3 px-4 text-ink transition-colors";
    const optBtn = (active) => `p-5 rounded-xl border text-left transition-colors ${active ? "bg-verde text-white border-verde" : "bg-hueso text-ink border-linea hover:border-madera"}`;

    return (
        <div className="bg-crema min-h-screen">
            <Navbar />
            <div className="pt-24 md:pt-28 pb-24 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-mut hover:text-verde mb-6">
                    <ArrowLeft size={16} /> Volver
                </button>
                <h1 className="font-display font-extrabold text-4xl md:text-6xl text-ink tracking-tight mb-10">Finalizar pedido</h1>

                <form onSubmit={submit} className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7 space-y-9">
                        <div>
                            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-madera mb-4">01 · Tus datos</h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <input data-testid="checkout-nombre" className={inputCls} placeholder="Nombre y apellido *" value={form.nombre} onChange={set("nombre")} />
                                <input data-testid="checkout-telefono" className={inputCls} placeholder="Teléfono / WhatsApp *" value={form.telefono} onChange={set("telefono")} />
                                <input data-testid="checkout-email" className={inputCls} placeholder="Correo (opcional)" value={form.email} onChange={set("email")} />
                                {!retira && <input data-testid="checkout-ciudad" className={inputCls} placeholder="Ciudad" value={form.ciudad} onChange={set("ciudad")} />}
                                {!retira && <input data-testid="checkout-direccion" className={`${inputCls} sm:col-span-2`} placeholder="Dirección de envío" value={form.direccion} onChange={set("direccion")} />}
                            </div>

                            {/* Entrega: quien retira en tienda no debería llenar dirección ni GPS */}
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                {[{ id: "envio", t: "Envío a domicilio", d: "Te lo llevamos" },
                                  { id: "retiro", t: "Retiro en tienda", d: "Pasas por El Llanito" }].map((o) => (
                                    <button type="button" key={o.id} data-testid={`entrega-${o.id}`}
                                        onClick={() => setForm((f) => ({ ...f, entrega: o.id }))} className={optBtn(form.entrega === o.id)}>
                                        <span className="font-display font-bold text-lg">{o.t}</span>
                                        <p className="text-xs opacity-70 mt-1">{o.d}</p>
                                    </button>
                                ))}
                            </div>
                            {retira && (
                                <p className="text-[11px] text-mut mt-2">
                                    Te avisamos por WhatsApp cuando tu pedido esté listo para retirar en El Llanito, Caracas.
                                </p>
                            )}
                            {/* Ubicación exacta: el repartidor navega con Waze, no con la dirección escrita.
                                No aplica a quien retira en tienda. */}
                            <div className={`mt-4 bg-hueso border border-linea rounded-xl p-4${retira ? " hidden" : ""}`}>
                                <p className="text-xs font-semibold text-ink flex items-center gap-1.5"><MapPin size={14} className="text-naranja" /> Ubicación exacta para la entrega (opcional)</p>
                                <p className="text-[11px] text-mut mt-1">Comparte tu ubicación y el repartidor llega directo con Waze.</p>
                                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                                    <button type="button" data-testid="checkout-ubicacion-btn" onClick={capturarUbicacion} className="shrink-0 flex items-center justify-center gap-1.5 bg-verde text-white rounded-full px-4 py-2.5 text-xs font-semibold hover:bg-verde-800 transition-colors">
                                        {ubiEstado === "ok" ? <><Check size={14} /> Ubicación capturada</> : <><MapPin size={14} /> Compartir mi ubicación</>}
                                    </button>
                                    <input data-testid="checkout-ubicacion-link" className={`${inputCls} flex-1 !py-2.5 text-sm`} placeholder="…o pega un link de Google Maps / Waze" value={ubi} onChange={(e) => { setUbi(e.target.value); setUbiEstado(e.target.value ? "ok" : ""); }} />
                                </div>
                                {ubiEstado && ubiEstado !== "ok" && <p className="text-[11px] text-naranja mt-2">{ubiEstado}</p>}
                                {ubiEstado === "ok" && ubi.startsWith("https://waze.com") && (
                                    <a href={ubi} target="_blank" rel="noreferrer" className="inline-block text-[11px] text-verde font-semibold mt-2 underline">Ver el punto en Waze ↗</a>
                                )}
                            </div>
                        </div>

                        <div>
                            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-madera mb-4">02 · Tipo de compra</h2>
                            <div className="grid grid-cols-2 gap-4">
                                {[{ id: "detal", t: "Detal", d: "Compra al menudeo" }, { id: "mayoreo", t: "Mayoreo", d: "Compra por volumen" }].map((o) => (
                                    <button type="button" key={o.id} data-testid={`tipo-${o.id}`} onClick={() => setForm((f) => ({ ...f, tipo: o.id }))} className={optBtn(form.tipo === o.id)}>
                                        <span className="font-display font-bold text-lg">{o.t}</span>
                                        <p className="text-xs opacity-70 mt-1">{o.d}</p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-madera mb-4">03 · Método de pago</h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                {PAYMENT_METHODS.map((m) => (
                                    <button type="button" key={m.id} data-testid={`metodo-${m.id}`} onClick={() => setMetodo(m.id)} className={optBtn(metodo === m.id)}>
                                        <span className="font-display font-bold text-base">{m.label}</span>
                                        <p className="text-xs opacity-70 mt-1">{m.desc}</p>
                                    </button>
                                ))}
                            </div>
                            {/* Datos para pagar del método elegido, con copiado al toque:
                                transcribir a mano una cuenta de 20 dígitos en el teléfono
                                es donde se cae el pedido. */}
                            {pagos[metodo]?.instrucciones && (
                                <div className="mt-4 bg-hueso border border-linea rounded-xl p-4" data-testid="datos-pago">
                                    <p className="text-xs font-bold text-ink mb-2.5">
                                        Datos para pagar · {PAYMENT_METHODS.find((m) => m.id === metodo)?.label}
                                    </p>
                                    <div className="space-y-1.5">
                                        {pagos[metodo].instrucciones.split("\n").filter(Boolean).map((linea, i) => {
                                            // Solo se ofrece copiar lo que es un dato (teléfono, cuenta,
                                            // RIF/cédula), no las frases explicativas. El prefijo de letra
                                            // del RIF/cédula va incluido: un Pago Móvil sin la J- rebota.
                                            const dato = (linea.match(/\b[JVEGPjvegp]-?[\d][\d\s.-]{5,}|\b[\d][\d\s.-]{6,}/) || [])[0];
                                            return (
                                                <div key={i} className="flex items-start gap-2">
                                                    <span className="text-[13px] text-ink flex-1 leading-snug">{linea}</span>
                                                    {dato && (
                                                        <button type="button" onClick={() => copiar(dato.trim())}
                                                            data-testid={`copiar-${i}`}
                                                            className="shrink-0 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-verde border border-linea rounded-full px-2.5 py-1 hover:border-verde transition-colors">
                                                            {copiado === dato.trim() ? <><Check size={11} /> Copiado</> : <><Copy size={11} /> Copiar</>}
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="flex items-center justify-between gap-3 border-t border-linea mt-3 pt-3">
                                        <span className="text-xs text-mut">Monto exacto a pagar</span>
                                        <div className="flex items-center gap-2">
                                            <span className="font-display font-bold text-lg text-verde">{fmt(total)}</span>
                                            <button type="button" onClick={() => copiar(total.toFixed(2))}
                                                data-testid="copiar-monto"
                                                className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-verde border border-linea rounded-full px-2.5 py-1 hover:border-verde transition-colors">
                                                {copiado === total.toFixed(2) ? <><Check size={11} /> Copiado</> : <><Copy size={11} /> Copiar</>}
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-[11px] text-mut mt-2.5">
                                        Puedes pagar ahora y subir el comprobante al confirmar, o confirmar primero y pagar después: te llevamos a la página de tu pedido.
                                    </p>
                                </div>
                            )}
                            <p className="text-[11px] text-mut mt-3">Al confirmar, coordinamos el pago y el envío por WhatsApp. Precios en USD; pago en Bs a tasa BCV del día.</p>
                        </div>

                        <div>
                            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-madera mb-4">04 · Notas (opcional)</h2>
                            <textarea data-testid="checkout-notas" className={`${inputCls} min-h-24`} placeholder="Instrucciones especiales, referencias…" value={notas} onChange={(e) => setNotas(e.target.value)} />
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="lg:sticky lg:top-28 hero-gradient text-white rounded-2xl p-6 md:p-8 shadow-lift">
                            <h2 className="font-display font-bold text-2xl mb-6">Resumen</h2>
                            <div className="space-y-4 max-h-72 overflow-y-auto pr-2">
                                {items.map((it) => (
                                    <div key={it.product.id} className="flex gap-3 items-center">
                                        <div className="h-14 w-14 bg-white rounded-lg shrink-0 overflow-hidden"><ProductImage src={it.product.foto_url} alt="" className="h-full w-full object-contain p-1" /></div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs line-clamp-1">{it.product.nombre}</p>
                                            <p className="text-[11px] text-madera">{it.quantity} × {fmt(unitPrice(it.product, it.quantity))}</p>
                                        </div>
                                        <span className="text-sm">{fmt(unitPrice(it.product, it.quantity) * it.quantity)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="border-t border-white/15 mt-6 pt-6 space-y-2">
                                {savings > 0 && <div className="flex justify-between text-xs text-madera"><span>Ahorro mayoreo</span><span>− {fmt(savings)}</span></div>}
                                <div className="flex justify-between items-end pt-1">
                                    <span className="text-xs uppercase tracking-widest text-white/60">Total</span>
                                    <span className="text-right">
                                        <span className="block text-xs opacity-70">{fmt(subtotal)} + IVA {fmt(iva)}</span>
                                        <span data-testid="checkout-total" className="font-display font-extrabold text-4xl">{fmt(total)}</span>
                                    </span>
                                </div>
                            </div>
                            <button type="submit" disabled={loading} data-testid="place-order-btn" className="w-full mt-6 bg-naranja text-white rounded-full py-4 font-semibold hover:bg-naranja-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                                {loading ? <><Loader2 size={16} className="animate-spin" /> Procesando…</> : "Confirmar pedido"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
